//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  FiniteDuration,
  InstagramReelsPostCustomizations,
} from '@bvengineering/universal-scheduling-gateway-service-types';
import moment from 'moment-timezone';

import { IWeekHourlyHeatmap } from '@apis/gateway-instagram/instagramAudienceMetricsModel';
import { UniversalScheduledPostConfigType } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { FACEBOOK_SCHEDULE_POST_TYPES } from '@facebook/constants/facebookConstants';
import Cuid from '@models/Cuid';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import {
  IInstagramStoryGroupClipCustomization,
  InstagramStoryGroupClipCustomizationType,
} from '@models/IScheduledInstagramStoryGroup';
import { IShopTag } from '@models/IShopTag';
import { ITaggedUser } from '@models/ITaggedUser';
import TimeZoneId from '@models/TimeZoneId';
import { DEFAULT_FACEBOOK_POST_DETAILS_FORM_STATE } from '@universal-scheduling/components/scheduler/post-details-forms/facebook/FacebookPostDetailsConstants';
import {
  DEFAULT_INSTAGRAM_POST_DETAILS_AUTOMATIC_FORM_STATE,
  DEFAULT_INSTAGRAM_POST_DETAILS_MANUAL_FORM_STATE,
  DEFAULT_INSTAGRAM_REELS_DETAILS_AUTOMATIC_FORM_STATE,
  DEFAULT_INSTAGRAM_REELS_DETAILS_MANUAL_FORM_STATE,
  DEFAULT_INSTAGRAM_STORY_DETAILS_AUTOMATIC_FORM_STATE,
  DEFAULT_INSTAGRAM_STORY_DETAILS_MANUAL_FORM_STATE,
  IReelsCreativeNote,
} from '@universal-scheduling/components/scheduler/post-details-forms/instagram/InstagramPostDetailsConstants';
import { OrderedInstagramReelsCreativeNotesTypes } from '@universal-scheduling/components/scheduler/post-details-forms/instagram/reels-customizations/InstagramReelsPostDetailsCustomizationsConstants';
import { DEFAULT_PINTEREST_POST_DETAILS_STATE } from '@universal-scheduling/components/scheduler/post-details-forms/pinterest/PinterestPostDetailsConstants';
import {
  DEFAULT_FRAMES_PER_SECOND,
  MAX_SHOP_TAG_COUNT,
} from '@universal-scheduling/components/scheduler/post-details-forms/postDetailsFormConstants';
import { IRankedOnlineFollowerHour } from '@universal-scheduling/components/scheduler/post-details-forms/shared/recommended-times/RecommendedTimesConstants';
import { DEFAULT_TIKTOK_POST_DETAILS_STATE } from '@universal-scheduling/components/scheduler/post-details-forms/tiktok/TikTokPostDetailsConstants';
import { DEFAULT_TWITTER_POST_DETAILS_FORM_STATE } from '@universal-scheduling/components/scheduler/post-details-forms/twitter/TwitterPostDetailsConstants';
import { DEFAULT_YOUTUBE_POST_DETAILS_FORM_STATE } from '@universal-scheduling/components/scheduler/post-details-forms/youtube/YouTubePostDetailsConstants';
import { arrayUnorderedEquals, difference } from '@utils/arrayUtils';
import { isDefined, isEmptyValue, isUndefined } from '@utils/filterPredicates';
import { groupBy } from '@utils/groupBy';
import { DAYS_OF_WEEK_ABBR } from '@utils/moment';
import { reduceToRecord } from '@utils/reduceToRecord';
import { rotateArrayLeft } from '@utils/rotateArray';
import { parseFacebookText } from '@utils/social-text-utils/facebookTextUtils';
import { SocialTextEntityType } from '@utils/social-text-utils/socialTextUtilsModel';
import { getUrlWithSecureProtocol } from '@utils/urlUtils';
import isValidUrl, {
  hasValidProtocol,
  isCuralateShortLink,
} from '@utils/urlValidation';

export const getDefaultPostDetailsFormState = (
  postType: UniversalPostType,
  title?: string,
) => {
  // TODO PD-243507: Set automatic or manual default form state based on user settings
  switch (postType) {
    case UniversalPostType.FacebookPageFeedPost:
      return DEFAULT_FACEBOOK_POST_DETAILS_FORM_STATE;
    case UniversalPostType.TikTokVideoAutomaticPost:
      return DEFAULT_TIKTOK_POST_DETAILS_STATE;
    case UniversalPostType.PinterestOrganicPin:
      return { ...DEFAULT_PINTEREST_POST_DETAILS_STATE, title };
    case UniversalPostType.TwitterMediaPost:
      return DEFAULT_TWITTER_POST_DETAILS_FORM_STATE;
    case UniversalPostType.InstagramFeedAutomaticPost:
      return DEFAULT_INSTAGRAM_POST_DETAILS_AUTOMATIC_FORM_STATE;
    case UniversalPostType.InstagramFeedManualPost:
      return DEFAULT_INSTAGRAM_POST_DETAILS_MANUAL_FORM_STATE;
    case UniversalPostType.InstagramReelsAutomaticPost:
      return DEFAULT_INSTAGRAM_REELS_DETAILS_AUTOMATIC_FORM_STATE;
    case UniversalPostType.InstagramReelsManualPost:
      return DEFAULT_INSTAGRAM_REELS_DETAILS_MANUAL_FORM_STATE;
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
      return DEFAULT_INSTAGRAM_STORY_DETAILS_AUTOMATIC_FORM_STATE;
    case UniversalPostType.InstagramStoryGroupPost:
      return DEFAULT_INSTAGRAM_STORY_DETAILS_MANUAL_FORM_STATE;
    case UniversalPostType.YouTubeAutomaticPost:
      return { ...DEFAULT_YOUTUBE_POST_DETAILS_FORM_STATE, title };
  }
};

export const isShopTag = (tag: IShopTag | ITaggedUser): tag is IShopTag => {
  return 'productId' in tag;
};

// gets the number of rows that shop tags will occupy given number of columns to display
export const getRowCountForMediaItemShopTags = (
  numTags: number,
  numColumns: number = 2,
) => {
  const remainderTags = numTags % numColumns;
  if (remainderTags !== 0) {
    return Math.floor(numTags / numColumns) + 1;
  }
  return numTags / numColumns;
};

// gets total number of rows that a shop tag group will occupy given number of columns to display
export const getTotalRowCountForShopTagGroup = (
  groupedTags: [string, IShopTag[]][],
  numColumns: number = 2,
) =>
  groupedTags.reduce((count, [_, shopTags]) => {
    const rowCount = getRowCountForMediaItemShopTags(
      shopTags.length,
      numColumns,
    );
    return count + rowCount;
  }, 0);

// determines which products will be displayed and which will be hidden until user clicks on View All
export const getGroupedTaggedProductsForForm = (
  groupedTags: [string, IShopTag[]][],
  maxRows: number,
  numColumns: number = 2,
) =>
  groupedTags.reduce<{
    hidden: [string, IShopTag[]][];
    rowCount: number;
    visible: [string, IShopTag[]][];
  }>(
    (accum, [mediaIndexText, mediaTaggedProducts]) => {
      // number of rows the media item's shop tags will occupy
      const rows = getRowCountForMediaItemShopTags(
        mediaTaggedProducts.length,
        numColumns,
      );
      const baseAccum = { ...accum, rowCount: accum.rowCount + rows };

      if (rows + accum.rowCount <= maxRows) {
        // all of media item's tags can fit on screen
        return {
          ...baseAccum,
          visible: [...accum.visible, [mediaIndexText, mediaTaggedProducts]],
        };
      } else {
        // this media item's tags will be partially visible/hidden
        const numTagsToAddToVisible = (maxRows - accum.rowCount) * numColumns;
        if (numTagsToAddToVisible > 0) {
          return {
            ...baseAccum,
            visible: [
              ...accum.visible,
              [
                mediaIndexText,
                mediaTaggedProducts.slice(0, numTagsToAddToVisible),
              ],
            ],
            hidden: [
              ...accum.hidden,
              [
                mediaIndexText,
                mediaTaggedProducts.slice(numTagsToAddToVisible),
              ],
            ],
          };
        } else {
          // we're already over the row limit so all tags for this media item will be hidden
          return {
            ...baseAccum,
            hidden: [...accum.hidden, [mediaIndexText, mediaTaggedProducts]],
          };
        }
      }
    },
    { rowCount: 0, visible: [], hidden: [] },
  );

export const getShopTagsErrors = (taggedProducts: IShopTag[] | undefined) => {
  const shopTagsByMedia = groupBy(
    taggedProducts ?? [],
    (shopTag) => shopTag.mediaIndex,
  );

  return Object.entries(shopTagsByMedia).reduce<string[]>(
    (accum, [mediaIndex, shopTags]) => {
      if (shopTags.length > MAX_SHOP_TAG_COUNT) {
        accum.push((+mediaIndex + 1).toString());
      }
      return accum;
    },
    [],
  );
};

// The backend expects and returns the videoThumbailOffset in milliseconds
export const VIDEO_OFFSET_CONVERSION_FACTOR = 1000;
export const videoThumbnailOffsetSecondsToFiniteDuration = (
  offsetSeconds?: number,
): FiniteDuration => ({
  length: (offsetSeconds ?? 0) * VIDEO_OFFSET_CONVERSION_FACTOR,
  unit: 'MILLISECONDS',
  finite: true,
});

export const getFacebookScheduledPostType = (
  photoCuids?: Cuid[],
  videoCuid?: Cuid,
  link?: string,
): {
  configType:
    | UniversalScheduledPostConfigType.FacebookPageFeedPhotoPost
    | UniversalScheduledPostConfigType.FacebookPageFeedVideoPost
    | UniversalScheduledPostConfigType.FacebookPageFeedLinkPost;
  postType?: FACEBOOK_SCHEDULE_POST_TYPES;
  cuids: Cuid[];
} => {
  if (isDefined(photoCuids) && photoCuids.length > 0) {
    return {
      configType: UniversalScheduledPostConfigType.FacebookPageFeedPhotoPost,
      postType:
        photoCuids.length > 1
          ? FACEBOOK_SCHEDULE_POST_TYPES.MULTI_IMAGE
          : FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_IMAGE,
      cuids: photoCuids,
    };
  }
  if (isDefined(videoCuid)) {
    return {
      configType: UniversalScheduledPostConfigType.FacebookPageFeedVideoPost,
      postType: FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_VIDEO,
      cuids: [videoCuid],
    };
  }
  return {
    configType: UniversalScheduledPostConfigType.FacebookPageFeedLinkPost,
    postType: isDefined(link) ? FACEBOOK_SCHEDULE_POST_TYPES.LINK : undefined,
    cuids: [],
  };
};

export function rotateHourlyHeatMapByHours(
  heatMap: IWeekHourlyHeatmap,
  deltaHours: number,
): IWeekHourlyHeatmap {
  if (deltaHours === 0) {
    return heatMap;
  }

  // Take hour week-long heatmap and turn it into a 2D array of Days X Hours
  const linearHeatMap = DAYS_OF_WEEK_ABBR.map((day) => heatMap[day]);

  // Flatten this into a 1D array of 24*7 hours.
  const flattenedHeatMap = linearHeatMap.flat();

  // Rotate the array by the number of hours delta we have.
  const rotatedFlattenedHeatMap = rotateArrayLeft(flattenedHeatMap, deltaHours);

  // Build the labeled heatmap again
  return reduceToRecord(
    DAYS_OF_WEEK_ABBR.map((day, dayIndex) => ({ day, dayIndex })),
    ({ day, dayIndex }) => {
      // Grab chunks of 24 hours at a time.
      const startIdx = dayIndex * 24;
      return [day, rotatedFlattenedHeatMap.slice(startIdx, startIdx + 24)];
    },
  );
}

export function rotateHourlyHeatmapToTimezone(
  onlineFollowers: IWeekHourlyHeatmap,
  heatmapTimeZone: TimeZoneId,
  timeZone: TimeZoneId,
) {
  // Get the UTC offset of each timezone as of today, in hours.
  const momentZone = moment.tz.zone(timeZone);
  const heatMapMomentZone = moment.tz.zone(heatmapTimeZone);
  const targetOffset = Math.floor(
    (isEmptyValue(momentZone) ? Date.now() : momentZone.utcOffset(Date.now())) /
      60,
  );
  const startingOffset = Math.floor(
    (isEmptyValue(heatMapMomentZone)
      ? Date.now()
      : heatMapMomentZone.utcOffset(Date.now())) / 60,
  );

  // How many hours we need to shift our heatmap by
  const hourDelta = targetOffset - startingOffset;

  return rotateHourlyHeatMapByHours(onlineFollowers, hourDelta);
}

/**
 * Given the reach values for each hour of the day, find the top 3 values.
 * @param hourlyReachHistogram Array of 24 reach values, one per hour of day.
 * @returns An array of the top 3 hours of the day & their metadata.
 */
export function getTop3TimesToPost(
  hourlyReachHistogram: number[],
): IRankedOnlineFollowerHour[] {
  return (
    hourlyReachHistogram
      // zip with index as hour of day.
      .map((reach, hourOfDay) => ({ reach, hourOfDay }))
      // Sort by reach, descending.
      .sort((a, b) => b.reach - a.reach)
      // Get top 3 entries
      .slice(0, 3)
      // By this point, we have the hours of day sorted by rank
      // But we want them ordered by hour of day, with a rank associated.
      // Zip with index as rank
      .map((hourReach, rankIndex) => ({ ...hourReach, rank: rankIndex + 1 }))
      // Sort by hour of day, ascending.
      .sort((a, b) => a.hourOfDay - b.hourOfDay)
  );
}

/**
 * If the input time has elapsed, return "Now + 1 minute"  instead.
 * This is used to handle scheduling edge cases.
 */
export const getNowIfTimeHasElapsed = (time: Date) => {
  if (time < new Date()) {
    return moment().add(1, 'minute').toDate();
  }
  return time;
};

export const getStoryCustomizationsIndex = (
  type: InstagramStoryGroupClipCustomizationType,
  customizations: IInstagramStoryGroupClipCustomization[],
) =>
  customizations.findIndex(
    ({ customizationType }) => customizationType === type,
  );

export const getStoryCustomizationsValue = (
  type: InstagramStoryGroupClipCustomizationType,
  customizations: IInstagramStoryGroupClipCustomization[],
) =>
  customizations.find(({ customizationType }) => customizationType === type)
    ?.value;

export const getStoryCustomizationsIndexAndValue = (
  type: InstagramStoryGroupClipCustomizationType,
  customizations: IInstagramStoryGroupClipCustomization[],
) => ({
  index: getStoryCustomizationsIndex(type, customizations),
  value: getStoryCustomizationsValue(type, customizations),
});

export const getUrlsFromCaption = (caption: string) => {
  return parseFacebookText(caption)
    .filter((entity) => entity.type === SocialTextEntityType.Url)
    .map((url) => url.value);
};

// Copied from facebookSchedulePostUtils.ts
export const getUpdatedPostUrl = (
  urlsInCaption: string[],
  urlsInPreviousCaption: string[],
  postUrl?: string,
): string | undefined => {
  const link = (() => {
    // There are no links in the new caption, return undefined
    if (urlsInCaption.length === 0) {
      return undefined;
      // The list of links in the old and new caption are the same, return the current postUrl
    } else if (arrayUnorderedEquals(urlsInPreviousCaption, urlsInCaption)) {
      return postUrl;
    } else {
      // A link was either created, edited, or deleted
      const newUrls = difference(urlsInCaption, urlsInPreviousCaption);

      // If there is a link in the new array that wasn't in the old array,
      // it is a new link or an edited link, return it
      if (newUrls.length > 0) {
        return newUrls[newUrls.length - 1];
      }

      // If there aren't any new urls, one was deleted, return the last url in the new array
      return urlsInCaption[urlsInCaption.length - 1];
    }
  })();

  return isUndefined(link) || hasValidProtocol(link)
    ? link
    : getUrlWithSecureProtocol(link);
};

/**
 * This returns the shortcode (last pathname) from a curalate shortlink.
 * For example, `cur.lt/lpjaxn0n0` would return `lpjaxn0n0`
 *
 * Copied from facebookShortLinkUtils.ts
 *
 */
const getShortCode = (shortLink: string) => {
  const indexOfLastPath = shortLink.lastIndexOf('/');
  return shortLink.slice(indexOfLastPath + 1);
};

/**
 * This returns an array of shortcodes for shortlinks in a scheduled post's caption.
 * For example, if the caption is '#cool cur.lt/lpjaxn0n0 cur.lt/123 http://www.fun.com' the returned shortcodes would
 * be ['lpjaxn0n0', '123']
 *
 * Copied from facebookShortLinkUtils.ts
 *
 */
export const getShortCodesFromCaption = (caption: string) => {
  return parseFacebookText(caption)
    .filter(
      (captionItem) =>
        captionItem.type === 'Url' &&
        isValidUrl(captionItem.value) &&
        isCuralateShortLink(captionItem.value),
    )
    .map((urlItem) => getShortCode(urlItem.value));
};

export const getCreativeNotes = (
  customizations: InstagramReelsPostCustomizations | undefined,
) => {
  if (!isDefined(customizations)) {
    return [];
  }

  return OrderedInstagramReelsCreativeNotesTypes.reduce<IReelsCreativeNote[]>(
    (acc, noteType) => {
      const notes = customizations[noteType];

      if (isDefined(notes)) {
        if (Array.isArray(notes)) {
          let arrayNotes: IReelsCreativeNote[] = [];
          notes.forEach((note, index) => {
            arrayNotes = [
              ...arrayNotes,
              {
                type: noteType,
                index,
                value: note,
              },
            ];
          });

          return [...acc, ...arrayNotes];
        } else {
          return [...acc, { type: noteType, value: notes }];
        }
      }

      return acc;
    },
    [],
  );
};

export const framesPerSecondFromMediaItem = (
  mediaItem: IMlsMediaItem | undefined,
) => {
  const numFrames = mediaItem?.networkItem?.mediaMetadata?.numFrames;
  const durationMillis = mediaItem?.networkItem?.mediaMetadata?.durationMillis;

  const framesPerSecond =
    isDefined(numFrames) && isDefined(durationMillis)
      ? numFrames / (durationMillis / VIDEO_OFFSET_CONVERSION_FACTOR)
      : DEFAULT_FRAMES_PER_SECOND;

  return framesPerSecond;
};

export const splitCollaboratorUsernamesString = (
  usernameString: string,
): string[] =>
  Array.from(
    new Set(
      usernameString
        .split(/[,<>@]/)
        .filter((tag) => !isEmptyValue(tag))
        .map((tag) => tag.trim().toLowerCase()),
    ),
  );
